import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatRp } from "@/lib/api";
import PasswordGeneratorModal from "@/components/PasswordGeneratorModal";
import {
  LayoutDashboard, Server, Tag, Receipt, KeyRound, Settings,
  LogOut, Wallet, Menu, X, ExternalLink
} from "lucide-react";

const nav = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/servers", icon: Server, label: "Server Saya" },
  { to: "/plans", icon: Tag, label: "Paket & Harga" },
  { to: "/billing", icon: Receipt, label: "Billing" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all
     ${isActive ? "btn-cyan text-white" : "text-slate-400 hover:text-white hover:bg-slate-800/50"}`;

  const SidebarContent = () => (
    <>
      <div className="flex items-center gap-3 px-2 mb-8">
        <img src="/logo.png" alt="logo" className="w-10 h-10 rounded-xl animate-float" />
        <div>
          <div className="font-heading font-extrabold text-white text-lg leading-none">Dreamer Hosting</div>
          <div className="text-[10px] uppercase tracking-widest text-cyan-400">Fast and Reliable</div>
        </div>
      </div>

      <nav className="space-y-1">
        {nav.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} className={linkClass}
            onClick={() => setMobileOpen(false)}
            data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}>
            <n.icon size={18} /> {n.label}
          </NavLink>
        ))}
        <button onClick={() => { setPwOpen(true); setMobileOpen(false); }}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800/50 transition-all"
          data-testid="nav-password-gen">
          <KeyRound size={18} /> Password Generator
        </button>
        {user?.role === "admin" && (
          <NavLink to="/admin" className={linkClass} onClick={() => setMobileOpen(false)} data-testid="nav-admin">
            <Settings size={18} /> Admin Settings
          </NavLink>
        )}
      </nav>

      <div className="mt-auto pt-6 space-y-2">
        <button onClick={async () => { await logout(); navigate("/login"); }}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-rose-400 hover:bg-rose-950/40 transition-all"
          data-testid="logout-btn">
          <LogOut size={18} /> Keluar
        </button>
      </div>
    </>
  );

  return (
    <div className="mc-bg pixel-grid min-h-screen flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 p-4 glass-strong border-r border-cyan-500/10 fixed h-screen z-20">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 p-4 glass-strong flex flex-col">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 lg:ml-64 relative z-10">
        {/* Topbar */}
        <header className="sticky top-0 z-30 glass border-b border-cyan-500/10 px-4 sm:px-6 py-3 flex items-center justify-between">
          <button className="lg:hidden text-slate-300" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
            <Menu size={22} />
          </button>
          <div className="hidden sm:block text-sm text-slate-400">
            Halo, <span className="text-white font-semibold">{user?.name || user?.email}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900/60 border border-cyan-500/20" data-testid="credit-badge">
              <Wallet size={15} className="text-cyan-400" />
              <span className="font-mono text-sm text-cyan-300 font-semibold">{formatRp(user?.credits)}</span>
            </div>
            <div className="w-9 h-9 rounded-full btn-cyan flex items-center justify-center text-white font-bold text-sm">
              {(user?.name || user?.email || "U")[0].toUpperCase()}
            </div>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 max-w-7xl">{children}</main>
      </div>

      <PasswordGeneratorModal open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
